import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { AumicoInformationPopupComponent, IPopupData, MAT_DIALOG_CUSTOM_CLASS } from '@modeso/aumico-lib-core-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep } from 'lodash-es';
import { IOpenedPrintOptionsAccordions } from 'projects/aumico-project-reports/src/app/interfaces/opened-print-options-accordions.interface';
import { AppStateDispatcher } from 'projects/aumico-project-reports/src/app/state/app.state.dispatcher';
import { distinctUntilChanged } from 'rxjs/operators';
import { IConsistencyCheckVisibility } from '../../interfaces/consistency-check-visibility.interface';
import { IEqualityCheckPrintPreviewTableData } from '../../interfaces/equality-check-print-preview-table-data';
import { PrintOptionsVisibility } from '../../types/print-options-visibility.type';
import { localizationKeys } from './../../constants/localizationKeys';

@UntilDestroy()
@Component({
    selector: 'equality-check-print-preview-table',
    templateUrl: 'equality-check-print-preview-table.component.html',
    styleUrls: ['equality-check-print-preview-table.component.scss'],
})
export class EqualityCheckPrintPreviewTable implements OnInit, OnChanges {
    consistencyCheckVisibility: IConsistencyCheckVisibility | undefined;
    isAccordionExpanded = false;
    isPreviousYearConsistent = false;
    public openedPrintOptionsAccordions: IOpenedPrintOptionsAccordions = {
        consistencyChecksAccordion: false,
        componentsAccordion: false,
        settingsAccordion: false,
        signatureAccordion: false,
        colorsAccordion: false,
    };

    constructor(
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private dialog: MatDialog,
        private readonly appStateDispatcher: AppStateDispatcher,
    ) {
        this.addSvgIcon('aumico-pattern-icon-blue-check');
        this.addSvgIcon('aumico-pattern-icon-black-wrong');
        this.addSvgIcon('aumico-pattern-icon-alert-info');
        this.addSvgIcon('aumico-pattern-icon-arrow-right');
        this.addSvgIcon('aumico-pattern-icon-arrow-down');
        this.appStateDispatcher
            .getOpenedPrintOptionsAccordions()
            .pipe(
                distinctUntilChanged((previous, current) => fastDeepEqual(previous, current)),
                untilDestroyed(this),
            )
            .subscribe((opened) => {
                this.openedPrintOptionsAccordions = cloneDeep(opened);
            });
    }

    @Input() equalityCheckDataPreviousYear: IEqualityCheckPrintPreviewTableData | null;
    @Input() equalityCheckDataCurrentYear: IEqualityCheckPrintPreviewTableData;
    @Input() noPreviousYear: boolean;
    @Input() set printOptionsVisibility(value: PrintOptionsVisibility | undefined) {
        this.consistencyCheckVisibility = value?.consistencyCheck;
    }
    @Input() currentYear: string | undefined;
    @Input() previousYear: string | undefined;

    ngOnInit(): void {
        // initial compute (ngOnChanges will already have run once before this)
        this.updatePreviousYearConsistency();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['equalityCheckDataPreviousYear'] || changes['noPreviousYear']) {
            this.updatePreviousYearConsistency();
        }
    }

    private updatePreviousYearConsistency(): void {
        if (!this.noPreviousYear && this.equalityCheckDataPreviousYear != null) {
            this.isPreviousYearConsistent = this.getIsPreviousYearConsistent();
        } else {
            this.isPreviousYearConsistent = false;
        }
    }

    openInformationDialog(): void {
        const popupData: IPopupData = {
            titleLocalizationKey: localizationKeys.aumico_information_dialog_title,
            isContentHtmlType: false,
            contentMessage: localizationKeys.aumico_reports_equality_check_print_preview_information_dialog_content,
        };

        this.dialog.open<AumicoInformationPopupComponent, IPopupData>(AumicoInformationPopupComponent, {
            width: '80%',
            data: popupData,
            panelClass: MAT_DIALOG_CUSTOM_CLASS,
        });
    }

    private addSvgIcon(name: string) {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    public onToggleAccordion(): void {
        this.isAccordionExpanded = !this.isAccordionExpanded;
        this.openedPrintOptionsAccordions.consistencyChecksAccordion = this.isAccordionExpanded;
        this.appStateDispatcher.setOpenedPrintOptionsAccordions(this.openedPrintOptionsAccordions);
    }

    private getIsPreviousYearConsistent(): boolean {
        const prev = this.equalityCheckDataPreviousYear!;
        if (!prev.isMappingAssetsAndLiabilitiesConsistent || !prev.isMappingProfitIncomeAndProfitFliConsistent) {
            return false;
        }

        const optionalChecks = [
            prev.isCashflowStatementTotalsConsistent,
            prev.isAdjustmentsAssetsAndLiabilitiesConsistent,
            prev.isAdjustmentsProfitIncomeAndProfitFliConsistent,
            // REV-20899: isReclasses…Consistent flags removed (transactional model is always balanced).
        ];

        // if any optional check is defined and false → inconsistent
        return !optionalChecks.some((flag) => flag === false);
    }
}
